import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Shield, Users, Search, User, LogOut, 
  ChevronLeft, ChevronRight, Map, ArrowUpCircle, ArrowDownCircle,
  Infinity, Calendar, Loader2, Hexagon, Octagon, Circle, Plus, Coins
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Create admin axios instance
const adminApi = axios.create({
  baseURL: API_BASE,
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('atlas_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default function AdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Upgrade modal state
  const [upgradeModal, setUpgradeModal] = useState({ open: false, user: null });
  const [upgradeDuration, setUpgradeDuration] = useState('infinite');
  const [upgradePlan, setUpgradePlan] = useState('PRO');
  const [customMonths, setCustomMonths] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  
  // Add credits modal state
  const [creditsModal, setCreditsModal] = useState({ open: false, user: null });
  const [creditsAmount, setCreditsAmount] = useState('');
  const [addingCredits, setAddingCredits] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (planFilter && planFilter !== 'all') params.append('plan', planFilter);
      params.append('page', page.toString());
      params.append('limit', '15');
      
      const response = await adminApi.get(`/api/admin/users?${params}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('atlas_admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  }, [search, planFilter, page, navigate]);

  const fetchStats = async () => {
    try {
      const response = await adminApi.get('/api/admin/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('atlas_admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchUsers();
    fetchStats();
  }, [fetchUsers, navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const openUpgradeModal = (user) => {
    setUpgradeModal({ open: true, user });
    setUpgradeDuration('infinite');
    setUpgradePlan('PRO');
    setCustomMonths('');
  };

  const openCreditsModal = (user) => {
    setCreditsModal({ open: true, user });
    setCreditsAmount('');
  };

  const handleUpgrade = async () => {
    if (!upgradeModal.user) return;
    
    setUpgrading(true);
    try {
      let months = null;
      if (upgradeDuration === 'custom' && customMonths) {
        months = parseInt(customMonths, 10);
      } else if (upgradeDuration !== 'infinite') {
        months = parseInt(upgradeDuration, 10);
      }
      
      await adminApi.post(`/api/admin/users/${upgradeModal.user.id}/upgrade`, {
        user_id: upgradeModal.user.id,
        months,
        plan: upgradePlan
      });
      
      toast.success(`${upgradeModal.user.name} upgraded to ${upgradePlan}${months ? ` for ${months} months` : ' (lifetime)'}`);
      setUpgradeModal({ open: false, user: null });
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error('Failed to upgrade user');
    } finally {
      setUpgrading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!creditsModal.user || !creditsAmount) return;
    
    const credits = parseInt(creditsAmount, 10);
    if (isNaN(credits) || credits <= 0) {
      toast.error('Please enter a valid positive number');
      return;
    }
    
    setAddingCredits(true);
    try {
      await adminApi.post(`/api/admin/users/${creditsModal.user.id}/add-credits`, {
        credits
      });
      
      toast.success(`Added ${credits} credits to ${creditsModal.user.name}`);
      setCreditsModal({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error('Failed to add credits');
    } finally {
      setAddingCredits(false);
    }
  };

  const handleDowngrade = async (user) => {
    if (!confirm(`Downgrade ${user.name} to FREE plan?`)) return;
    
    try {
      await adminApi.post(`/api/admin/users/${user.id}/downgrade`);
      toast.success(`${user.name} downgraded to FREE`);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error('Failed to downgrade user');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('atlas_admin_token');
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-red-500" />
            <span className="font-heading text-lg font-semibold">Atlas Admin</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleLogout}
            className="text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white border border-border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.total_users}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cobalt/10 flex items-center justify-center">
                  <Hexagon className="w-5 h-5 text-cobalt" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.pro_users}</p>
                  <p className="text-xs text-muted-foreground">Pro Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange/10 flex items-center justify-center">
                  <Octagon className="w-5 h-5 text-orange" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.max_users || 0}</p>
                  <p className="text-xs text-muted-foreground">Max Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Circle className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.free_users}</p>
                  <p className="text-xs text-muted-foreground">Free Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-border rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Map className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{stats.total_maps}</p>
                  <p className="text-xs text-muted-foreground">Total Maps</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-border rounded-lg p-4 mb-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="pl-10 h-10"
                data-testid="admin-search"
              />
            </div>
            <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-[140px] h-10">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="FREE">Free</SelectItem>
                <SelectItem value="PRO">Pro</SelectItem>
                <SelectItem value="MAX">Max</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-cobalt text-white hover:bg-cobalt/90 h-10">
              Search
            </Button>
          </form>
        </div>

        {/* Users Table */}
        <div className="bg-white border border-border rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Credits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Joined</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-sm">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.plan === 'MAX' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange/10 text-orange border border-orange/20">
                          <Octagon className="w-3 h-3" />
                          MAX
                        </span>
                      ) : user.plan === 'PRO' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cobalt/10 text-cobalt border border-cobalt/20">
                          <Hexagon className="w-3 h-3" />
                          PRO
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          <Circle className="w-3 h-3" />
                          FREE
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {user.credits ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {user.pro_duration || (user.plan !== 'FREE' ? 'Lifetime' : '-')}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openCreditsModal(user)}
                          className="border-border text-muted-foreground hover:bg-muted h-7 text-xs"
                          data-testid={`credits-btn-${user.id}`}
                        >
                          <Coins className="w-3 h-3 mr-1" />
                          Add Credits
                        </Button>
                        {user.plan === 'FREE' ? (
                          <Button
                            size="sm"
                            onClick={() => openUpgradeModal(user)}
                            className="bg-cobalt hover:bg-cobalt/90 text-white h-7 text-xs"
                            data-testid={`upgrade-btn-${user.id}`}
                          >
                            <ArrowUpCircle className="w-3 h-3 mr-1" />
                            Upgrade
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDowngrade(user)}
                            className="border-border text-muted-foreground hover:bg-muted h-7 text-xs"
                            data-testid={`downgrade-btn-${user.id}`}
                          >
                            <ArrowDownCircle className="w-3 h-3 mr-1" />
                            Downgrade
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-border flex items-center justify-between bg-muted/20">
            <p className="text-sm text-muted-foreground">
              Showing {users.length} of {total} users
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Upgrade Modal */}
      <Dialog open={upgradeModal.open} onOpenChange={(open) => setUpgradeModal({ open, user: upgradeModal.user })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-cobalt" />
              Upgrade User
            </DialogTitle>
            <DialogDescription>
              Upgrade <span className="text-foreground font-medium">{upgradeModal.user?.name}</span> to a paid plan
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Plan</label>
              <Select value={upgradePlan} onValueChange={setUpgradePlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRO">
                    <div className="flex items-center gap-2">
                      <Hexagon className="w-4 h-4 text-cobalt" />
                      Pro (100 credits)
                    </div>
                  </SelectItem>
                  <SelectItem value="MAX">
                    <div className="flex items-center gap-2">
                      <Octagon className="w-4 h-4 text-orange" />
                      Max (500 credits)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Duration</label>
              <Select value={upgradeDuration} onValueChange={setUpgradeDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="infinite">
                    <div className="flex items-center gap-2">
                      <Infinity className="w-4 h-4" />
                      Lifetime (Infinite)
                    </div>
                  </SelectItem>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                  <SelectItem value="6">6 Months</SelectItem>
                  <SelectItem value="12">12 Months</SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Custom Duration
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {upgradeDuration === 'custom' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Number of Months</label>
                <Input
                  type="number"
                  min="1"
                  max="120"
                  value={customMonths}
                  onChange={(e) => setCustomMonths(e.target.value)}
                  placeholder="Enter months..."
                />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setUpgradeModal({ open: false, user: null })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpgrade}
                disabled={upgrading || (upgradeDuration === 'custom' && !customMonths)}
                className={`flex-1 text-white ${upgradePlan === 'PRO' ? 'bg-cobalt hover:bg-cobalt/90' : 'bg-orange hover:bg-orange/90'}`}
              >
                {upgrading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  `Upgrade to ${upgradePlan}`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Credits Modal */}
      <Dialog open={creditsModal.open} onOpenChange={(open) => setCreditsModal({ open, user: creditsModal.user })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-green-600" />
              Add Credits
            </DialogTitle>
            <DialogDescription>
              Add credits to <span className="text-foreground font-medium">{creditsModal.user?.name}</span>
              <br />
              <span className="text-xs">Current credits: {creditsModal.user?.credits ?? 0}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Credits to Add</label>
              <Input
                type="number"
                min="1"
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
                placeholder="Enter amount..."
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {[10, 25, 50, 100, 500].map(amount => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => setCreditsAmount(String(amount))}
                  className="text-xs"
                >
                  +{amount}
                </Button>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setCreditsModal({ open: false, user: null })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCredits}
                disabled={addingCredits || !creditsAmount}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {addingCredits ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  `Add ${creditsAmount || 0} Credits`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
